import React, { useMemo, useState } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { CONTACT_TIMELINE_ROW_LIMIT_PER_OBJECT } from '@shared/flow/contact-timeline';
import type { Surface } from '@shared/analytics/events';
import { CompactUrlLink, SourceLinkIcon, SourceOpenButton } from '../links';
import { useFlowContactTimeline, useFlowContactSignals } from './hooks';
import { WorkflowsDropdown } from './ContactActionMenus';
import { CampaignMembershipIcon } from './CampaignMembershipIcon';
import { hasActionAffordance } from '../actions/ActionsTabList';
import { isLiveActionRow } from '../actions/action-fields';
import { SignalRowActions } from '../signals/SignalRowActions';
import { useMinuteToken } from '../signals/useMinuteToken';
import { useSignalsUiStore } from '../signals/useSignalsUiStore';
import {
  type EntryKind,
  type Entry,
  KIND_LABEL,
  TIMELINE_RENDER_CAP,
  normalizeTimelineEntries,
  fmtTimelineDate,
  countByKind,
  visibleEntries,
} from './contactTimelineEntries';

/**
 * Generic contact timeline — normalizes the six Middleman arrays PLUS Terra
 * Signal actions into one date-sorted stream with kind filter chips. Every
 * row — including `action` — uses the SAME collapsed header (date, dot,
 * title, one-line subtitle, expand chevron) and the same accordion body
 * (`EntryDetail`'s label/value list). `action` entries additionally get an
 * Enroll/Open affordance line above the header, mirroring the `workspace`
 * kind's "Open workspace" link, so acting on a row never requires expanding
 * it first.
 *
 * `'action'` interleaves with every other kind (2026-09-22, Spencer QA
 * feedback) — it is NOT a separate mode/tab. A prior iteration kept Actions
 * as its own chip with a dedicated `ActionsTabList` render path; that made a
 * contact's most recent activity (an action) invisible unless you happened to
 * click the Actions chip. A second iteration rendered actions via a bespoke
 * `ActionRow` with its own type/status pills — inconsistent with every other
 * kind's plain accordion look, and the pill wrapped illegibly. This is the
 * third: action rows ARE Entries, full stop — same header, same
 * expand-to-detail, answering "assigned to whom, what name/type/subtype, who
 * actioned it and when" only in the expanded body, not the collapsed row.
 */

const KIND_DOT: Record<EntryKind, string> = {
  signal: 'bg-content-action',
  touchpoint: 'bg-content-success',
  task: 'bg-content-warning',
  event: 'bg-content-secondary',
  campaign: 'bg-content-tertiary',
  workspace: 'bg-content-primary',
  action: 'bg-content-action',
};

/** Main/Separate badge for a workspace entry's collapsed row (U8). */
function WorkspaceBadge({ kind }: { kind: 'main' | 'separate' }) {
  return (
    <span
      className={`ml-1 px-1.5 py-0.5 rounded-full text-[9.5px] font-medium align-middle ${
        kind === 'main'
          ? 'bg-content-success/10 text-content-success dark:bg-content-success/20 dark:text-content-success'
          : 'bg-bg-secondary text-content-tertiary dark:bg-nightshade-700 dark:text-nightshade-400'
      }`}
    >
      {kind === 'main' ? 'Main' : 'Separate'}
    </span>
  );
}

/** Per-kind expanded detail: description paragraph + label/value rows. */
function EntryDetail({ entry }: { entry: Entry }) {
  const hasWorkspaceLink = entry.kind === 'workspace' && !!entry.workspaceUrl;
  const sourceLink = entry.kind === 'action' ? entry.signalRow?.source_link : null;
  if (!entry.description && entry.fields.length === 0 && !hasWorkspaceLink && !sourceLink) {
    return <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 italic">No further detail.</div>;
  }
  return (
    <div className="space-y-1">
      {entry.fields.map(f => (
        <div key={f.label} className="flex gap-1.5 text-[11.5px]">
          <span className="w-28 shrink-0 text-content-tertiary dark:text-nightshade-500">{f.label}</span>
          <span className="min-w-0 text-content-secondary dark:text-nightshade-300">{f.value}</span>
        </div>
      ))}
      {hasWorkspaceLink && (
        <div className="flex gap-1.5 text-[11.5px]">
          <span className="w-28 shrink-0 text-content-tertiary dark:text-nightshade-500">Workspace</span>
          <span className="min-w-0">
            <CompactUrlLink url={entry.workspaceUrl} label={entry.workspaceUrl!.replace(/^https:\/\//, '')} />
          </span>
        </div>
      )}
      {entry.description && entry.description !== entry.subtitle && (
        <p className="text-[11.5px] text-content-secondary dark:text-nightshade-300 leading-relaxed whitespace-pre-line pt-0.5">{entry.description}</p>
      )}
      {hasWorkspaceLink && (
        <div className="pt-1">
          <CompactUrlLink url={entry.workspaceUrl} label="Open workspace" />
        </div>
      )}
      {/* A clear, labeled button — not just a plain text link — matching
          AccountTimeline's SfdcOpenButton (Spencer QA, 2026-09-23: "this UI
          is great in the account timelines and should be followed here"). */}
      {sourceLink && <SourceOpenButton url={sourceLink} />}
    </div>
  );
}

export function ContactTimeline({
  contactId,
  accountId = null,
  contactEmail = null,
  // Default 'command_center' (not 'signals') so every EXISTING mount — none
  // of which pass this prop yet except `ContactDetailPanels.tsx`'s
  // `surface="signals"` thread-through below — renders byte-for-byte as
  // before (bead .16's Do-Not-Create: "no behaviour change ... when
  // surface !== 'signals'").
  surface = 'command_center',
}: { contactId: string; accountId?: string | null; contactEmail?: string | null; surface?: Surface }) {
  const { data, isLoading, error } = useFlowContactTimeline(contactId);
  // Actions merge into the same timeline (plan §2b item 4, D1, revised
  // 2026-09-22) — a separate fetch boundary from the Middleman-backed
  // timeline above, but the rows merge into `entries` below rather than
  // rendering through a separate mode. A `signalsError` here just means zero
  // action entries merge in; the rest of the timeline still renders (error
  // isolation, per the test list) — no separate error UI needed for it.
  const { data: signalsData, isLoading: signalsLoading } = useFlowContactSignals(contactId);

  // The verification-freshness clock (D-A) `SignalRowActions` needs on the
  // `surface="signals"` affordance line below — re-derives once a minute
  // (and immediately on tab-foreground) per `useMinuteToken`'s own doc,
  // never a bare `new Date()` read at render time.
  const minuteToken = useMinuteToken();
  const now = useMemo(() => new Date(minuteToken), [minuteToken]);
  const requestArchive = useSignalsUiStore(s => s.requestArchive);

  const [mode, setMode] = useState<EntryKind | null>(null);
  // A SET of expanded row ids (2026-08-28, Spencer) — was a single
  // `expandedId`, so at most one accordion row could ever be open.
  // Individual row clicks are now independently additive: opening row B no
  // longer auto-closes row A (mirrors AccountTimeline's same-day change).
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());

  const entries = useMemo(
    () => (data ? normalizeTimelineEntries(data, accountId, signalsData?.rows ?? []) : []),
    [data, accountId, signalsData],
  );
  // Defensive "+more" (U8): Middleman's resolve-contact-timeline is capped at
  // CONTACT_TIMELINE_ROW_LIMIT_PER_OBJECT (the shared constant src/api/flow.ts
  // sends as `rowLimitPerObject` — imported, never re-literalled, so bumping
  // the cap can't leave this check behind). An exact-cap return means there may
  // be MORE workspace memberships we simply didn't fetch, so this flags that
  // possibility rather than silently rendering the page as the whole list.
  // Checked against the raw wire array (not the normalized/filtered `entries`),
  // since the cap is about what the API returned, independent of any
  // client-side date/lifecycle filtering.
  const workspaceUsersAtCap = data
    ? data.workspaceUsers.length === CONTACT_TIMELINE_ROW_LIMIT_PER_OBJECT
    : false;
  const counts = useMemo(() => countByKind(entries), [entries]);
  const visible = visibleEntries(entries, mode);

  if (isLoading) return <div className="text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center">Loading timeline…</div>;
  if (error) return <div className="text-[12px] text-content-danger dark:text-red-400 py-4 text-center">Timeline failed to load.</div>;
  // "No activity yet." only when the merged entries list is empty — a
  // contact with zero Middleman-sourced entries but a live Terra Signal
  // (e.g. a fresh Job Change) is not "no activity". `!signalsLoading` guards
  // against flashing this while the still-in-flight signals fetch simply
  // hasn't landed yet (plan §2b item 4's empty-state fix).
  if (entries.length === 0 && !signalsLoading) {
    return <div className="text-[12px] text-content-tertiary dark:text-nightshade-500 py-4 text-center">No activity yet.</div>;
  }

  // Expand-all/collapse-all toggle (2026-08-28, Spencer) — scoped to the
  // rendered rows (`.slice(0, 50)`), since rows beyond the render cap can't
  // be expanded anyway. `action` rows are expandable like every other kind.
  const renderedIds = visible.slice(0, TIMELINE_RENDER_CAP).map(e => e.id);
  const allExpanded = renderedIds.length > 0 && renderedIds.every(id => expandedIds.has(id));
  // Union/subtract against the *rendered* ids only — never replace the whole
  // set. A row expanded while a different filter was active must stay
  // expanded when that filter is cleared, even though it wasn't part of
  // this toggle's rendered scope.
  const toggleExpandAll = () =>
    setExpandedIds(prev => {
      if (allExpanded) {
        const next = new Set(prev);
        for (const id of renderedIds) next.delete(id);
        return next;
      }
      return new Set([...prev, ...renderedIds]);
    });

  return (
    <div>
      <div className="flex gap-1 flex-wrap items-center mb-2">
        {(Object.keys(KIND_LABEL) as EntryKind[]).filter(k => counts[k]).map(k => (
          <button
            key={k}
            onClick={() => setMode(mode === k ? null : k)}
            className={`px-2 py-0.5 rounded-full border text-[10.5px] ${
              mode === k
                ? 'bg-content-primary text-white border-content-primary dark:bg-nightshade-100 dark:text-nightshade-900 dark:border-nightshade-100'
                : 'bg-white dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400 border-secondary dark:border-nightshade-700 hover:bg-bg-secondary dark:hover:bg-nightshade-700'
            }`}
          >
            {KIND_LABEL[k]} ({counts[k]})
          </button>
        ))}
        <button
          type="button"
          onClick={toggleExpandAll}
          aria-label={allExpanded ? 'Collapse all' : 'Expand all'}
          className="ml-auto px-2 py-0.5 rounded-full border text-[10.5px] bg-white dark:bg-nightshade-800 text-content-secondary dark:text-nightshade-400 border-secondary dark:border-nightshade-700 hover:bg-bg-secondary dark:hover:bg-nightshade-700"
        >
          {allExpanded ? 'Collapse all' : 'Expand all'}
        </button>
      </div>
      <div>
        {visible.slice(0, TIMELINE_RENDER_CAP).map(e => {
          const isExpanded = expandedIds.has(e.id);
          const toggleRow = () => setExpandedIds(prev => {
            const next = new Set(prev);
            if (isExpanded) next.delete(e.id); else next.add(e.id);
            return next;
          });
          // Live/open action rows get a light orange row tint — Spencer,
          // 2026-09-22: "the entire row should highlight orange if there's a
          // recent or live terra action." Scoped to the entry row itself
          // (data already loaded for this expanded contact) — the Contacts
          // LIST table's own row highlight is separate, deliberately
          // deferred work (would need an always-on terra_signals lookup on
          // that endpoint).
          //
          // The two-field rule itself lives in `isLiveActionRow`
          // (`actions/action-fields.ts`), shared with `AccountTimeline`'s
          // merged action rows so the two surfaces can't drift (U6/D9).
          const isLiveAction = e.kind === 'action' && e.signalRow && isLiveActionRow(e.signalRow);
          return (
            <div
              key={e.id}
              className={`border-b border-primary/40 dark:border-nightshade-800 last:border-b-0 ${isLiveAction ? 'bg-tangerine-50 dark:bg-tangerine-900/30' : ''}`}
            >
              {e.kind === 'workspace' && e.workspaceUrl && (
                <div className="flex items-center gap-1.5 pl-[52px] pt-0.5">
                  <CompactUrlLink url={e.workspaceUrl} label="Open workspace" />
                </div>
              )}
              {e.kind === 'action' && e.signalRow ? (
                // A DIV (not a real <button>) — mirrors AccountTimeline's own
                // clickable-row pattern exactly, since a real <button> can't
                // nest another interactive element (the inline link/enroll
                // icon below). Spencer QA (2026-09-23): "move the open
                // button so it's nested in the row... link icon if it's a
                // link, + icon if it's a queue enrollment... this UI is
                // great in the account timelines and should be followed
                // here."
                <div
                  role="button"
                  tabIndex={0}
                  aria-expanded={isExpanded}
                  onClick={toggleRow}
                  onKeyDown={(ev) => {
                    // Nested controls (the source-link icon, the inline
                    // WorkflowsDropdown enroll button) own their own
                    // activation — Enter/Space bubbling up from one of them
                    // must not ALSO toggle this row (R12).
                    if (ev.target !== ev.currentTarget) return;
                    if (ev.key === 'Enter' || ev.key === ' ') { ev.preventDefault(); toggleRow(); }
                  }}
                  className="w-full flex gap-2 py-1.5 text-[12px] text-left items-start hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded cursor-pointer"
                >
                  <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500 pt-0.5">{fmtTimelineDate(e.date)}</span>
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${KIND_DOT[e.kind]}`} />
                  <span className="min-w-0 flex-1 pt-px">
                    <span className="font-medium text-content-primary dark:text-nightshade-100">{e.title}</span>
                    {e.subtitle && <span className="text-content-tertiary dark:text-nightshade-500"> · {e.subtitle}</span>}
                  </span>
                  {surface === 'signals' && e.signalRow.signal_id.startsWith('signal_action:') ? (
                    // bead .16 — the Signals surface's own row-action matrix
                    // (Snooze/Bring back/Archive/verb), the SAME
                    // `getSignalRowActions` render `SignalRowActions` uses
                    // in the table's `rowAction` slot. `onActed` is a noop
                    // here (unlike the table): this Timeline line has no
                    // "next row" concept to auto-expand into — invalidating
                    // `FLOW_CONTACT_SIGNALS_KEY` (inside the mutation
                    // hooks themselves) is enough to make the row disappear.
                    // `now` is the shared minute-token clock (freshness
                    // gating, D-A) — never a bare `new Date()` read here.
                    // `onArchiveRequest` routes to the Signal card's inline
                    // archive form (QA batch 2 D-I, B8) via the store's
                    // one-shot `requestArchive` — the card sits in this
                    // same expanded contact card, reads the intent, opens
                    // and scrolls to its form. No dialog here.
                    <SignalRowActions row={e.signalRow} now={now} layout="compact" onActed={() => {}} onArchiveRequest={requestArchive} />
                  ) : e.signalRow.signal_type === 'Campaign' ? (
                    // Item H (U18/D10) — every campaign/event-membership row
                    // opens the member sidebar, regardless of `source_link`
                    // (NULL on tiers 2/4). Must come BEFORE the `source_link`
                    // branch below, or tier-1 rows (which DO have a
                    // `source_link`) would render `SourceLinkIcon` instead —
                    // the exact bug this branch order fixes. The SFDC record
                    // stays reachable one click away via the expanded
                    // detail's `SourceOpenButton` (unchanged).
                    <CampaignMembershipIcon row={e.signalRow} />
                  ) : e.signalRow.source_link ? (
                    <SourceLinkIcon url={e.signalRow.source_link} />
                  ) : hasActionAffordance(e.signalRow) ? (
                    <WorkflowsDropdown
                      contactId={e.signalRow.contact_id!}
                      contactName={e.signalRow.contact_name}
                      contactEmail={contactEmail}
                      iconOnly
                    />
                  ) : null}
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
                    : <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={toggleRow}
                  className="w-full flex gap-2 py-1.5 text-[12px] text-left hover:bg-bg-secondary/60 dark:hover:bg-nightshade-800/60 rounded"
                >
                  <span className="w-11 shrink-0 text-[11px] text-content-tertiary dark:text-nightshade-500 pt-px">{fmtTimelineDate(e.date)}</span>
                  <span className={`w-1.5 h-1.5 rounded-full mt-1.5 shrink-0 ${KIND_DOT[e.kind]}`} />
                  <span className="min-w-0 flex-1">
                    <span className="font-medium text-content-primary dark:text-nightshade-100">{e.title}</span>
                    {e.kind === 'workspace' && e.workspaceBadge && <WorkspaceBadge kind={e.workspaceBadge} />}
                    {e.subtitle && <span className="text-content-tertiary dark:text-nightshade-500"> · {e.subtitle}</span>}
                  </span>
                  {isExpanded
                    ? <ChevronDown className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />
                    : <ChevronRight className="w-3.5 h-3.5 shrink-0 mt-0.5 text-content-tertiary dark:text-nightshade-500" />}
                </button>
              )}
              {isExpanded && (
                <div className="pl-[52px] pb-2 pr-2">
                  <EntryDetail entry={e} />
                </div>
              )}
            </div>
          );
        })}
        {visible.length > 50 && (
          <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 pt-2 text-center">Showing 50 of {visible.length}</div>
        )}
        {workspaceUsersAtCap && (
          <div className="text-[11px] text-content-tertiary dark:text-nightshade-500 pt-2 text-center">
            +more workspace memberships may exist — showing the most recent {CONTACT_TIMELINE_ROW_LIMIT_PER_OBJECT}
          </div>
        )}
      </div>
    </div>
  );
}
